import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Plus, Download, Mail, Trash2, RefreshCw, Edit, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StandaloneActFormModal, { ACT_TYPE_LABELS, ACT_TYPES } from './StandaloneActFormModal';

import { standaloneActService } from '../../services/standaloneActService';
import { clientService } from '../../services/clientService';
import { serviceService } from '../../services/serviceService';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('ro-RO') : '-');

const getErrorMessage = (error, fallback) => error.response?.data?.message || fallback;

/**
 * Acte adiționale pentru contracte care nu sunt în aplicație (legacy).
 */
const StandaloneActsPage = () => {
  const queryClient = useQueryClient();

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingAct, setEditingAct] = useState(null);
  const [sendingAct, setSendingAct] = useState(null);
  const [sendForm, setSendForm] = useState({ emails: '', subject: '', message: '' });
  const [busyId, setBusyId] = useState(null);

  const { data: acts = [], isLoading, refetch } = useQuery('standaloneActs', standaloneActService.getAll, {
    onError: () => toast.error('Eroare la încărcarea actelor adiționale'),
  });
  const { data: clients = [] } = useQuery('clients', clientService.getAll);
  const { data: catalogServices = [] } = useQuery('services', serviceService.getAll);

  const refreshList = () => queryClient.invalidateQueries('standaloneActs');

  const openCreate = () => {
    setEditingAct(null);
    setShowFormModal(true);
  };

  const openEdit = (act) => {
    setEditingAct(act);
    setShowFormModal(true);
  };

  const handleSaved = () => {
    setShowFormModal(false);
    setEditingAct(null);
    refreshList();
  };

  const runAction = async (actId, action) => {
    try {
      setBusyId(actId);
      await action();
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = (act) => runAction(act.id, async () => {
    try {
      const { blob, filename } = await standaloneActService.downloadPdf(act.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      refreshList();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Eroare la descărcarea PDF-ului'));
    }
  });

  const handleRegenerate = (act) => runAction(act.id, async () => {
    try {
      await standaloneActService.generatePdf(act.id);
      toast.success('PDF regenerat cu succes');
      refreshList();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Eroare la generarea PDF-ului'));
    }
  });

  const handleDelete = (act) => {
    if (!window.confirm(`Ștergi actul adițional nr. ${act.number}${act.clientName ? ` (${act.clientName})` : ''}? PDF-ul generat va fi șters și el.`)) {
      return;
    }
    runAction(act.id, async () => {
      try {
        await standaloneActService.delete(act.id);
        toast.success('Actul adițional a fost șters');
        refreshList();
      } catch (error) {
        toast.error(getErrorMessage(error, 'Eroare la ștergerea actului adițional'));
      }
    });
  };

  const openSend = (act) => {
    const emails = [act.variables?.clientEmail, act.variables?.representativeEmail]
      .filter((email) => email && email.includes('@'));
    setSendForm({ emails: [...new Set(emails)].join(', '), subject: '', message: '' });
    setSendingAct(act);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const emails = sendForm.emails.split(/[,\s;]+/).map((email) => email.trim()).filter(Boolean);
    if (emails.length === 0) {
      toast.error('Specificați cel puțin o adresă de email');
      return;
    }

    await runAction(sendingAct.id, async () => {
      try {
        await standaloneActService.send(sendingAct.id, {
          emails,
          subject: sendForm.subject || null,
          message: sendForm.message || null,
        });
        toast.success(`Actul adițional a fost trimis către ${emails.length} destinatar${emails.length !== 1 ? 'i' : ''}`);
        setSendingAct(null);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Eroare la trimiterea email-ului'));
      }
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Se încarcă..." />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Acte Adiționale Standalone</h1>
          <p className="text-gray-600 mt-1">
            Acte adiționale pentru contracte care nu sunt în aplicație (ex: contracte legacy). Toate datele se completează în formular.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" icon={RefreshCw} onClick={() => refetch()}>
            Reîmprospătează
          </Button>
          <Button variant="primary" icon={Plus} onClick={openCreate}>
            Act adițional nou
          </Button>
        </div>
      </div>

      {/* Listă */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        {acts.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <FileText className="w-10 h-10 mx-auto mb-3 text-gray-400" />
            Nu există acte adiționale standalone. Apasă „Act adițional nou” pentru a crea unul.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Act</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Tip</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Beneficiar</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Contract</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Acțiuni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {acts.map((act) => (
                <tr key={act.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">nr. {act.number}</div>
                    <div className="text-gray-500">{formatDate(act.date)}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {ACT_TYPE_LABELS[act.actType] || ACT_TYPE_LABELS[ACT_TYPES.PRELUNGIRE]}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{act.clientName || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{act.contractNumber ? `nr. ${act.contractNumber}` : '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => handleDownload(act)} disabled={busyId === act.id} className="p-2 text-green-600 hover:text-green-800 disabled:opacity-50" title="Descarcă PDF">
                        <Download size={16} />
                      </button>
                      <button onClick={() => handleRegenerate(act)} disabled={busyId === act.id} className="p-2 text-gray-600 hover:text-gray-800 disabled:opacity-50" title="Regenerează PDF">
                        <RefreshCw size={16} />
                      </button>
                      <button onClick={() => openEdit(act)} disabled={busyId === act.id} className="p-2 text-blue-600 hover:text-blue-800 disabled:opacity-50" title="Editează">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => openSend(act)} disabled={busyId === act.id} className="p-2 text-indigo-600 hover:text-indigo-800 disabled:opacity-50" title="Trimite pe email">
                        <Mail size={16} />
                      </button>
                      <button onClick={() => handleDelete(act)} disabled={busyId === act.id} className="p-2 text-red-600 hover:text-red-800 disabled:opacity-50" title="Șterge">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <StandaloneActFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        act={editingAct}
        clients={clients}
        catalogServices={catalogServices}
        onSaved={handleSaved}
      />

      {/* Trimitere pe email */}
      <Modal
        isOpen={!!sendingAct}
        onClose={() => setSendingAct(null)}
        title={sendingAct ? `Trimite Act Adițional nr. ${sendingAct.number}` : ''}
        size="md"
      >
        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destinatari *</label>
            <textarea
              rows={2}
              value={sendForm.emails}
              onChange={(e) => setSendForm((prev) => ({ ...prev, emails: e.target.value }))}
              placeholder="exemplu@scoala.ro, director@scoala.ro"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">Separați adresele prin virgulă sau spațiu. PDF-ul se atașează automat.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subiect (opțional)</label>
            <input
              type="text"
              value={sendForm.subject}
              onChange={(e) => setSendForm((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder={sendingAct ? `Act adițional nr. ${sendingAct.number}${sendingAct.contractNumber ? ` la contractul nr. ${sendingAct.contractNumber}` : ''}` : ''}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mesaj (opțional)</label>
            <textarea
              rows={4}
              value={sendForm.message}
              onChange={(e) => setSendForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Lăsat gol, se trimite un mesaj standard cu rugămintea de a returna actul semnat."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => setSendingAct(null)}>Anulează</Button>
            <Button type="submit" variant="primary" icon={Mail} loading={busyId === sendingAct?.id} disabled={busyId === sendingAct?.id}>
              Trimite
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default StandaloneActsPage;
